import React from 'react';
import { Outlet } from 'react-router-dom';
import { Box } from '@mui/system';
// material
import { styled } from '@mui/material/styles';

import Header from '../layouts/manager/dashboard/header/Header';
import Navbar from '../layouts/manager/dashboard/navbar/Navbar';
import { useMutation, useQuery } from 'react-fetching-library';
import { getManagerApplicationsAction, markNewDocumentsAsRead, uploadCheckboxSectionFileAction, getEmployeeApplications } from '../services/request';
import { CircularProgress } from '@mui/material';
import toast from 'react-hot-toast';
import { NewDocumentAddedDialog } from './staff/NewDocumentAddedDialog';

const RootStyle = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  overflow: 'hidden',
  margin: '0 5px'
});

const HeaderStyle = styled('div')(({ theme }) => ({
  padding: '20px 0px',
  [theme.breakpoints.up('lg')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

// ----------------------------------------------------------------------
const ManagerContext = React.createContext([null, _ => {}]);

export default () => {
  const { loading, payload, error } = useQuery(getManagerApplicationsAction());
  const { mutate } = useMutation(markNewDocumentsAsRead);
  const uploadFileMutation = useMutation(uploadCheckboxSectionFileAction);
  const [applications, setApplications] = React.useState([]);
  const [application, setApplication] = React.useState(null);
  const [weekPeriod, setWeekPeriod] = React.useState(null);
  const [managerEmployees, setManagerEmployees] = React.useState([]);
  const [managerEmployee, setManagerEmployee] = React.useState(null);
  const [choices, setChoices] = React.useState({});
  const [showNewDocumentAddedDialog, setShowNewDocumentAddedDialog] = React.useState(false);
  const [tabsType, setTabsType] = React.useState('manager'); // albo staff
  const { loading: optionsLoading, payload: optionsPayload, error: optionsError } = useQuery(getEmployeeApplications('OPTIONS'));

  const updateApplication = async () => {
    const { payload, error, loading: qLoading } = await mutate(application.id);
    if (!error && !qLoading) {
      setApplication(prev => ({ ...prev, new_document_added_by_office: false }));
    }
  };

  const uploadCheckboxSectionFile = async (checkboxSectionId, formData) => {
    formData.append('new_document_added_by_staff', true);
    const { error, payload } = await uploadFileMutation.mutate(formData, checkboxSectionId);

    if (!error) {
      setApplication(prev => ({ ...prev, checkbox_sections: [...prev.checkbox_sections.map(cs => cs.id == payload.id ? payload : cs)] }));

      return true;
    }

    return false;
  };

  React.useEffect(() => {
    if (!error && !loading) {
      if (payload.length > 0) {
        setApplications(payload);
        setApplication(payload[0]);
        if (payload[0].new_document_added_by_office) {
          setShowNewDocumentAddedDialog(true);
        }
      }
    }
    if (error) {
      toast.error('Wystąpił błąd');
    }
  }, [loading]);

  React.useEffect(() => {
    if (!optionsError && !optionsLoading) {
      setChoices(optionsPayload.actions.GET.choices);
    }
  }, [optionsLoading]);

  return (
    <ManagerContext.Provider value={{
      applications,
      setApplications,
      application,
      setApplication,
      uploadCheckboxSectionFile,
      managerEmployee,
      setManagerEmployee,
      managerEmployees,
      setManagerEmployees,
      weekPeriod,
      setWeekPeriod,
      choices,
      tabsType,
      setTabsType
    }}>
      {loading
        ? <Box height={300}>
          <CircularProgress />
        </Box>
        : <RootStyle>
          <HeaderStyle>
            <Header />
          </HeaderStyle>
          <MainStyle>
            <Navbar />
            <Box height={20} />
            {showNewDocumentAddedDialog && <NewDocumentAddedDialog
              manager={true}
              open={showNewDocumentAddedDialog}
              setOpen={setShowNewDocumentAddedDialog}
              updateApplication={updateApplication}
            />}
            <Outlet />
          </MainStyle>
        </RootStyle>
      }
    </ManagerContext.Provider>
  );
};

export const useManager = () => React.useContext(ManagerContext);
